import { moveTowards2, wrapAngle } from '../math.js';
import type { Surface } from '../map/types.js';
import type { Collider, PhysicsWorld } from './world.js';

export const CHARACTER = {
  RADIUS: 0.4,
  HEIGHT: 1.7,
  WALK_SPEED: 5.2,
  RUN_SPEED: 8.4,
  CARRY_SPEED_MULT: 0.72,
  GROUND_ACCEL: 55,
  AIR_ACCEL: 16,
  ICE_ACCEL: 3.2,
  STUN_ACCEL: 9,
  JUMP_VELOCITY: 11,
  GRAVITY: 30,
  MAX_FALL_SPEED: 45,
  STEP_HEIGHT: 0.45,
  COYOTE_TIME: 0.12,
  JUMP_PAD_VELOCITY: 22,
  TURN_SPEED: 14,
} as const;

/** Estado físico completo de un personaje. Se transmite al propietario para la reconciliación. */
export interface CharacterState {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** Orientación visual del personaje. */
  yaw: number;
  grounded: boolean;
  groundId: string | null;
  /** Superficie bajo los pies (se recalcula cada paso). */
  groundSurface?: Surface;
  coyote: number;
  /** Segundos restantes de aturdimiento (tras un empujón o resbalón). */
  stun: number;
}

/** Intención de movimiento del jugador para un paso fijo. */
export interface MoveInput {
  seq: number;
  /** Ejes del joystick/teclado en el marco de la cámara: x = derecha, z = adelante. Rango [-1, 1]. */
  mx: number;
  mz: number;
  /** Yaw de la cámara en radianes. */
  yaw: number;
  jump: boolean;
  sprint: boolean;
}

export interface SlipperyZone {
  x: number;
  z: number;
  r: number;
}

/** Modificadores de mundo y de jugador que afectan a un paso. */
export interface StepModifiers {
  gravityScale: number;
  windX: number;
  windZ: number;
  slippery: SlipperyZone[];
  speedMult: number;
  canSprint: boolean;
}

export const DEFAULT_MODIFIERS: StepModifiers = {
  gravityScale: 1,
  windX: 0,
  windZ: 0,
  slippery: [],
  speedMult: 1,
  canSprint: true,
};

export interface StepResult {
  /** Empujes contra cajas: el servidor decide si la caja se mueve. */
  pushes: Array<{ id: string; dx: number; dz: number }>;
  landed: boolean;
  landingSpeed: number;
  jumped: boolean;
  launched: boolean;
  onIce: boolean;
}

export const createCharacterState = (x: number, y: number, z: number, yaw = 0): CharacterState => ({
  x,
  y,
  z,
  vx: 0,
  vy: 0,
  vz: 0,
  yaw,
  grounded: false,
  groundId: null,
  coyote: 0,
  stun: 0,
});

export const cloneCharacterState = (s: CharacterState): CharacterState => ({ ...s });

const hits: Collider[] = [];
const EPS = 0.001;

/** Sanea un input recibido para evitar valores fuera de rango o NaN. */
export function sanitizeInput(i: MoveInput): MoveInput {
  const len = Math.hypot(i.mx, i.mz);
  const k = len > 1 ? 1 / len : 1;
  return {
    seq: i.seq,
    mx: Number.isFinite(i.mx) ? i.mx * k : 0,
    mz: Number.isFinite(i.mz) ? i.mz * k : 0,
    yaw: Number.isFinite(i.yaw) ? wrapAngle(i.yaw) : 0,
    jump: !!i.jump,
    sprint: !!i.sprint,
  };
}

/**
 * Avanza un personaje un paso fijo. Determinista: cliente y servidor obtienen el mismo resultado
 * con el mismo estado, input, mundo y modificadores. El mundo debe estar en el instante t = world.time.
 */
export function stepCharacter(
  s: CharacterState,
  input: MoveInput,
  world: PhysicsWorld,
  mods: StepModifiers,
  dt: number,
): StepResult {
  const C = CHARACTER;
  const res: StepResult = { pushes: [], landed: false, landingSpeed: 0, jumped: false, launched: false, onIce: false };

  s.stun = Math.max(0, s.stun - dt);
  s.coyote = s.grounded ? C.COYOTE_TIME : Math.max(0, s.coyote - dt);

  resolveOverlap(s, world);

  // Arrastre por plataforma móvil
  if (s.grounded && s.groundId) {
    const [dx, dy, dz] = world.platformDelta(s.groundId, world.time - dt, world.time);
    if (dx || dy || dz) {
      s.x += dx;
      s.z += dz;
      s.y += Math.max(dy, -0.5);
      resolveOverlap(s, world);
    }
  }

  // Superficie
  let onIce = false;
  for (const z of mods.slippery) if (Math.hypot(s.x - z.x, s.z - z.z) < z.r && s.y < 2) onIce = true;
  if (s.grounded && s.groundSurface === 'ice') onIce = true;
  res.onIce = onIce && s.grounded;

  // Velocidad deseada en el marco de la cámara (forward = -Z rotado por yaw)
  const sin = Math.sin(input.yaw);
  const cos = Math.cos(input.yaw);
  const wishX = input.mx * cos - input.mz * sin;
  const wishZ = -input.mx * sin - input.mz * cos;
  const stunned = s.stun > 0;
  const speed = (input.sprint && mods.canSprint ? C.RUN_SPEED : C.WALK_SPEED) * mods.speedMult;
  const targetX = stunned ? 0 : wishX * speed;
  const targetZ = stunned ? 0 : wishZ * speed;
  const accel = stunned ? C.STUN_ACCEL : !s.grounded ? C.AIR_ACCEL : res.onIce ? C.ICE_ACCEL : C.GROUND_ACCEL;
  [s.vx, s.vz] = moveTowards2(s.vx, s.vz, targetX, targetZ, accel * dt);

  s.vx += mods.windX * dt;
  s.vz += mods.windZ * dt;

  // Gravedad y salto
  s.vy = Math.max(-C.MAX_FALL_SPEED, s.vy - C.GRAVITY * mods.gravityScale * dt);
  if (input.jump && !stunned && (s.grounded || s.coyote > 0) && s.vy <= 0.5) {
    s.vy = C.JUMP_VELOCITY * (mods.gravityScale < 1 ? 0.85 : 1);
    s.grounded = false;
    s.groundId = null;
    s.coyote = 0;
    res.jumped = true;
  }

  const wasGrounded = s.grounded;
  const fallSpeed = -s.vy;

  moveHorizontal(s, world, 'x', s.vx * dt, wasGrounded, res);
  moveHorizontal(s, world, 'z', s.vz * dt, wasGrounded, res);
  moveVertical(s, world, s.vy * dt);

  if (s.grounded && !wasGrounded) {
    res.landed = true;
    res.landingSpeed = fallSpeed;
  }

  if (s.grounded && s.groundSurface === 'jumpPad') {
    s.vy = C.JUMP_PAD_VELOCITY;
    s.grounded = false;
    s.groundId = null;
    s.coyote = 0;
    res.launched = true;
  }

  // Orientación visual hacia la dirección del movimiento
  const hSpeed = Math.hypot(s.vx, s.vz);
  if (hSpeed > 0.5 && !stunned) {
    const target = Math.atan2(-s.vx, -s.vz);
    s.yaw = wrapAngle(s.yaw + wrapAngle(target - s.yaw) * Math.min(1, C.TURN_SPEED * dt));
  }
  return res;
}

function charBox(s: CharacterState, x = s.x, y = s.y, z = s.z) {
  const r = CHARACTER.RADIUS;
  return [x - r, y, z - r, x + r, y + CHARACTER.HEIGHT, z + r] as const;
}

function moveHorizontal(s: CharacterState, world: PhysicsWorld, axis: 'x' | 'z', d: number, grounded: boolean, res: StepResult) {
  if (d === 0) return;
  const r = CHARACTER.RADIUS;
  const nx = axis === 'x' ? s.x + d : s.x;
  const nz = axis === 'z' ? s.z + d : s.z;
  const [a, b, c, e, f, g] = charBox(s, nx, s.y, nz);
  world.query(a, b + EPS, c, e, f, g, hits);
  if (hits.length === 0) {
    s.x = nx;
    s.z = nz;
    return;
  }
  // Intento de subir escalón
  const top = Math.max(...hits.map((h) => h.maxY));
  if (grounded && top - s.y <= CHARACTER.STEP_HEIGHT && top > s.y) {
    const [a2, b2, c2, e2, f2, g2] = charBox(s, nx, top + EPS, nz);
    if (world.isFree(a2, b2, c2, e2, f2, g2)) {
      s.x = nx;
      s.z = nz;
      s.y = top + EPS;
      return;
    }
  }
  // Bloqueo: pegarse a la cara del obstáculo más cercano
  let pos = axis === 'x' ? nx : nz;
  for (const h of hits) {
    if (h.kind === 'crate') res.pushes.push({ id: h.id, dx: axis === 'x' ? d : 0, dz: axis === 'z' ? d : 0 });
    if (axis === 'x') pos = d > 0 ? Math.min(pos, h.minX - r - EPS) : Math.max(pos, h.maxX + r + EPS);
    else pos = d > 0 ? Math.min(pos, h.minZ - r - EPS) : Math.max(pos, h.maxZ + r + EPS);
  }
  const cur = axis === 'x' ? s.x : s.z;
  // Nunca retroceder más allá de la posición actual (evita «tirones» si ya hay solape)
  if (d > 0) pos = Math.max(cur, pos);
  else pos = Math.min(cur, pos);
  if (axis === 'x') {
    s.x = pos;
    s.vx = 0;
  } else {
    s.z = pos;
    s.vz = 0;
  }
}

function moveVertical(s: CharacterState, world: PhysicsWorld, d: number) {
  const ny = s.y + d;
  const [a, , c, e, , g] = charBox(s);
  const minY = Math.min(s.y, ny) - (d <= 0 ? 0.05 : 0);
  const maxY = Math.max(s.y, ny) + CHARACTER.HEIGHT;
  world.query(a + EPS, minY, c + EPS, e - EPS, maxY, g - EPS, hits);
  if (d <= 0) {
    // Buscar el suelo más alto por debajo de los pies
    let floor = -Infinity;
    let floorC: Collider | null = null;
    for (const h of hits) {
      if (h.maxY <= s.y + EPS * 2 + 0.05 && h.maxY > floor) {
        floor = h.maxY;
        floorC = h;
      }
    }
    if (floorC && ny <= floor + 0.02) {
      s.y = floor;
      s.vy = 0;
      s.grounded = true;
      s.groundId = floorC.kind === 'platform' ? floorC.id : null;
      s.groundSurface = floorC.surface;
      return;
    }
    s.y = ny;
    s.grounded = false;
    s.groundId = null;
    s.groundSurface = undefined;
  } else {
    let ceil = Infinity;
    for (const h of hits) if (h.minY >= s.y + CHARACTER.HEIGHT - EPS && h.minY < ceil) ceil = h.minY;
    if (ny + CHARACTER.HEIGHT > ceil) {
      s.y = ceil - CHARACTER.HEIGHT - EPS;
      s.vy = 0;
    } else s.y = ny;
    s.grounded = false;
    s.groundId = null;
    s.groundSurface = undefined;
  }
}

/** Expulsa al personaje si un colisionador dinámico se ha metido dentro de él. */
export function resolveOverlap(s: CharacterState, world: PhysicsWorld) {
  for (let iter = 0; iter < 3; iter++) {
    const [a, b, c, e, f, g] = charBox(s);
    world.query(a, b + EPS, c, e, f, g, hits);
    if (hits.length === 0) return;
    const h = hits[0];
    const pens = [
      { ax: 'x+', v: h.maxX - a },
      { ax: 'x-', v: e - h.minX },
      { ax: 'z+', v: h.maxZ - c },
      { ax: 'z-', v: g - h.minZ },
      { ax: 'y+', v: h.maxY - s.y },
    ];
    pens.sort((p, q) => p.v - q.v);
    const p = pens[0];
    if (p.ax === 'x+') s.x += p.v + EPS;
    else if (p.ax === 'x-') s.x -= p.v + EPS;
    else if (p.ax === 'z+') s.z += p.v + EPS;
    else if (p.ax === 'z-') s.z -= p.v + EPS;
    else s.y += p.v + EPS;
  }
}
